import { CodeableConcept, Observation, ObservationComponent, Practitioner, Reference } from 'fhir/r4b';
import { VitalsSchema } from '../helpers/vitals/config-schema';
import { getVitalObservationFhirComponentInterpretations } from '../helpers/vitals/utils';
import {
  VitalBloodPressureObservationMethod,
  VitalFieldNames,
  VitalHeartbeatObservationMethod,
  VitalsOxygenSatObservationMethod,
  VitalTemperatureObservationMethod,
} from '../types/api/chart-data/chart-data.constants';
import {
  VitalsBaseObservationDTO,
  VitalsBloodPressureObservationDTO,
  VitalsBMIObservationDTO,
  VitalsDotVisionScreening,
  VitalsHeartbeatObservationDTO,
  VitalsHeightObservationDTO,
  VitalsLastMenstrualPeriodObservationDTO,
  VitalsObservationDTO,
  VitalsOxygenSatObservationDTO,
  VitalsRespirationRateObservationDTO,
  VitalsTemperatureObservationDTO,
  VitalsVisionObservationDTO,
  VitalsVisionOption,
  VitalsWeightObservationDTO,
  VitalsWeightOption,
  VitalsWeightPatientRefusedDTO,
} from '../types/api/chart-data/chart-data.types';
import { PATIENT_VITALS_META_SYSTEM } from '../types/api/chart-data/chart-data.types';
import { ObservationDTO } from '../types/data/screening-questions/types';
import { PRIVATE_EXTENSION_BASE_URL } from './constants';
import { getFullName } from './patient';

export const SNOMED_SYSTEM = 'http://snomed.info/sct';
export const LOINC_SYSTEM = 'http://loinc.org';

export const VITAL_TEMPERATURE_OBS_METHOD_CODE_ORAL = '89003005';
export const VITAL_TEMPERATURE_OBS_METHOD_CODE_RECTAL = '18649001';
export const VITAL_TEMPERATURE_OBS_METHOD_LOINC_CODE_AXILLARY = '8328-7';
export const VITAL_TEMPERATURE_OBS_METHOD_LOINC_CODE_TEMPORAL = '75539-7';
export const VITAL_TEMPERATURE_OBS_METHOD_LOINC_CODE_EAR = '76011-6';

export const VITAL_HEARTBEAT_OBS_METHOD_CODE_SITTING = '33586001';
export const VITAL_HEARTBEAT_OBS_METHOD_CODE_STANDING = '10904000';
export const VITAL_HEARTBEAT_OBS_METHOD_CODE_SUPINE = '40199007';

export const VITAL_BLOOD_PRESSURE_OBS_METHOD_CODE_SITTING = '33586001';
export const VITAL_BLOOD_PRESSURE_OBS_METHOD_CODE_STANDING = '10904000';
export const VITAL_BLOOD_PRESSURE_OBS_METHOD_CODE_SUPINE = '40199007';

export const VITAL_SYSTOLIC_BLOOD_PRESSURE_LOINC_CODE = '8480-6';
export const VITAL_DIASTOLIC_BLOOD_PRESSURE_LOINC_CODE = '8462-4';

export const VITAL_OXY_SATURATION_OBS_METHOD_CODE_ROOM_AIR = '250590007';
export const VITAL_OXY_SATURATION_OBS_METHOD_CODE_SUPPLEMENTAL_O2 = '250591006';

export const VITAL_LEFT_EYE_SNOMED_CODE = '8976008';
export const VITAL_RIGHT_EYE_SNOMED_CODE = '8977004';
export const VITAL_BOTH_EYES_SNOMED_CODE = '51440002';

export const VITAL_VISION_CHILD_TOO_YOUNG_OPTION_SNOMED_CODE = '9876543';
export const VITAL_VISION_WITH_GLASSES_OPTION_SNOMED_CODE = '1234567';
export const VITAL_VISION_WITHOUT_GLASSES_OPTION_SNOMED_CODE = '7654321';

// DOT (FMCSA form MCSA-5875) vision screening component codes.
// Color vision and monocular vision are modelled the FHIR-idiomatic way: the component code names
// the observable being assessed, and the answer is a coded value (valueCodeableConcept) using
// standard SNOMED CT findings/observables rather than a bare boolean. The specialist-referral item
// and the "received referral documentation" item are encoded as plain booleans (valueBoolean). The
// referral document itself is a real FHIR DocumentReference linked from the Observation via
// derivedFrom (see getDotVisionDocumentDerivedFrom / extractDotVisionScreening).

// Horizontal field of vision (degrees), modelled as Visual field + Horizontal qualifier + per-eye
// anatomical structure, with the measurement as a valueQuantity.
export const VITAL_VISION_DOT_VISUAL_FIELD_SNOMED_CODE = '73750009'; // Visual field (observable entity)
export const VITAL_VISION_DOT_HORIZONTAL_QUALIFIER_CODE = '24020000'; // Horizontal (qualifier value)
export const VITAL_VISION_DOT_LEFT_EYE_STRUCTURE_CODE = '1290031003'; // Structure of left eye proper (body structure)
export const VITAL_VISION_DOT_RIGHT_EYE_STRUCTURE_CODE = '1290032005'; // Structure of right eye proper (body structure)

// Color vision: observable as the component code, normal/abnormal finding as the value.
export const VITAL_VISION_DOT_COLOR_RECOGNITION_SNOMED_CODE = '363981009'; // Color vision, function (observable entity)
export const VITAL_VISION_DOT_COLOR_NORMAL_CODE = '827066007'; // Normal color vision (finding)
export const VITAL_VISION_DOT_COLOR_ABNORMAL_CODE = '23289000'; // Abnormal color vision (finding)

// Monocular vision: the monocular-vision observable as the component code, monocular/binocular
// observable as the value (yes = monocular, no = binocular).
export const VITAL_VISION_DOT_MONOCULAR_OBSERVABLE_CODE = '54055001'; // Monocular vision, function (observable entity)
export const VITAL_VISION_DOT_BINOCULAR_OBSERVABLE_CODE = '68362006'; // Binocular vision (observable entity)

// Specialist referral (the fact the applicant was referred).
export const VITAL_VISION_DOT_REFERRAL_SNOMED_CODE = '3457005'; // Patient referral (procedure)

export const VITAL_VISION_DOT_LOCAL_SYSTEM = `${PRIVATE_EXTENSION_BASE_URL}/dot-vision-screening`;
export const VITAL_VISION_DOT_RECEIVED_DOC_CODE = 'received-referral-documentation';

export const VITAL_WEIGHT_PATIENT_REFUSED_OPTION_SNOMED_CODE = '8675309';

export const VITAL_LAST_MENSTRUAL_PERIOD_LOINC_CODE = '8665-2';
export const VITAL_LAST_MENSTRUAL_PERIOD_UNSURE_OPTION_SNOMED_CODE = '261665006';

export const VITAL_BMI_LOINC_CODE = '39156-5';

export const getTempObservationMethodCodable = (
  tempDTO: VitalsTemperatureObservationDTO
): CodeableConcept | undefined => {
  const obsMethod = tempDTO?.observationMethod;

  if (!obsMethod) return;

  if (obsMethod === VitalTemperatureObservationMethod.Oral) {
    return {
      coding: [
        {
          system: SNOMED_SYSTEM,
          code: VITAL_TEMPERATURE_OBS_METHOD_CODE_ORAL,
          display: 'Oral temperature taking',
        },
      ],
    };
  }

  if (obsMethod === VitalTemperatureObservationMethod.Rectal) {
    return {
      coding: [
        {
          system: SNOMED_SYSTEM,
          code: VITAL_TEMPERATURE_OBS_METHOD_CODE_RECTAL,
          display: 'Rectal temperature taking',
        },
      ],
    };
  }

  if (obsMethod === VitalTemperatureObservationMethod.Axillary) {
    return {
      coding: [
        {
          system: LOINC_SYSTEM,
          code: VITAL_TEMPERATURE_OBS_METHOD_LOINC_CODE_AXILLARY,
          display: 'Axillary temperature',
        },
      ],
    };
  }

  if (obsMethod === VitalTemperatureObservationMethod.Temporal) {
    return {
      coding: [
        {
          system: LOINC_SYSTEM,
          code: VITAL_TEMPERATURE_OBS_METHOD_LOINC_CODE_TEMPORAL,
          display: 'Body temperature - Temporal artery',
        },
      ],
    };
  }

  if (obsMethod === VitalTemperatureObservationMethod.Ear) {
    return {
      coding: [
        {
          system: LOINC_SYSTEM,
          code: VITAL_TEMPERATURE_OBS_METHOD_LOINC_CODE_EAR,
          display: 'Ear temperature',
        },
      ],
    };
  }

  return undefined;
};

export const extractTemperatureObservationMethod = (
  observation: Observation
): VitalTemperatureObservationMethod | undefined => {
  const obsMethodCode = observation?.method?.coding?.at(0)?.code;
  if (!obsMethodCode) return;

  if (obsMethodCode === VITAL_TEMPERATURE_OBS_METHOD_CODE_ORAL) {
    return VitalTemperatureObservationMethod.Oral;
  }

  if (obsMethodCode === VITAL_TEMPERATURE_OBS_METHOD_CODE_RECTAL) {
    return VitalTemperatureObservationMethod.Rectal;
  }

  if (obsMethodCode === VITAL_TEMPERATURE_OBS_METHOD_LOINC_CODE_AXILLARY) {
    return VitalTemperatureObservationMethod.Axillary;
  }

  if (obsMethodCode === VITAL_TEMPERATURE_OBS_METHOD_LOINC_CODE_TEMPORAL) {
    return VitalTemperatureObservationMethod.Temporal;
  }

  if (obsMethodCode === VITAL_TEMPERATURE_OBS_METHOD_LOINC_CODE_EAR) {
    return VitalTemperatureObservationMethod.Ear;
  }

  return undefined;
};

export const getHeartbeatObservationMethodCodable = (
  heartbeatDTO: VitalsHeartbeatObservationDTO
): CodeableConcept | undefined => {
  const obsMethod = heartbeatDTO?.observationMethod;
  if (!obsMethod) return;
  if (obsMethod === VitalHeartbeatObservationMethod.Sitting) {
    return {
      coding: [
        {
          system: SNOMED_SYSTEM,
          code: VITAL_HEARTBEAT_OBS_METHOD_CODE_SITTING,
          display: 'Sitting position',
        },
      ],
    };
  }
  if (obsMethod === VitalHeartbeatObservationMethod.Standing) {
    return {
      coding: [
        {
          system: SNOMED_SYSTEM,
          code: VITAL_HEARTBEAT_OBS_METHOD_CODE_STANDING,
          display: 'Orthostatic body position',
        },
      ],
    };
  }
  if (obsMethod === VitalHeartbeatObservationMethod.Supine) {
    return {
      coding: [
        {
          system: SNOMED_SYSTEM,
          code: VITAL_HEARTBEAT_OBS_METHOD_CODE_SUPINE,
          display: 'Supine body position',
        },
      ],
    };
  }
  return undefined;
};

export const getBloodPressureObservationComponents = (
  bloodPressureDTO: VitalsBloodPressureObservationDTO,
  patientDOB: string | undefined,
  vitalsAlertConfig: VitalsSchema | undefined
): ObservationComponent[] => {
  const result: ObservationComponent[] = [];

  const componentAlerts =
    patientDOB && vitalsAlertConfig
      ? getVitalObservationFhirComponentInterpretations({
          vitalsObservation: bloodPressureDTO,
          patientDOB,
          patientSex: undefined,
          config: vitalsAlertConfig,
        })
      : {};

  const systolicPressure = bloodPressureDTO.systolicPressure;
  if (systolicPressure) {
    const systolicCode: CodeableConcept = {
      coding: [
        {
          system: SNOMED_SYSTEM,
          code: '271649006',
          display: 'Systolic blood pressure',
        },
        {
          system: LOINC_SYSTEM,
          code: VITAL_SYSTOLIC_BLOOD_PRESSURE_LOINC_CODE,
          display: 'Systolic blood pressure',
        },
      ],
    };
    const systolicInterpretation = componentAlerts?.['systolic-pressure'];
    const systolicComponent: ObservationComponent = {
      code: systolicCode,
      valueQuantity: { value: systolicPressure, system: 'http://unitsofmeasure.org', unit: 'mmHg' },
      interpretation: systolicInterpretation && systolicInterpretation.length > 0 ? systolicInterpretation : undefined,
    };

    result.push(systolicComponent);
  }

  const diastolicPressure = bloodPressureDTO.diastolicPressure;
  if (diastolicPressure) {
    const diastolicCode: CodeableConcept = {
      coding: [
        {
          system: LOINC_SYSTEM,
          code: VITAL_DIASTOLIC_BLOOD_PRESSURE_LOINC_CODE,
          display: 'Diastolic blood pressure',
        },
      ],
    };
    const diastolicInterpretation = componentAlerts?.['diastolic-pressure'];
    const diastolicComponent: ObservationComponent = {
      code: diastolicCode,
      valueQuantity: { value: diastolicPressure, system: 'http://unitsofmeasure.org', unit: 'mmHg' },
      interpretation:
        diastolicInterpretation && diastolicInterpretation.length > 0 ? diastolicInterpretation : undefined,
    };

    result.push(diastolicComponent);
  }

  return result;
};

export const extractBloodPressureValues = (
  components: ObservationComponent[]
): { systolicPressureValue?: number; diastolicPressureValue?: number } => {
  const systolicComponent = components.find((cmp) => {
    return cmp.code?.coding?.find((coding) => coding?.code === VITAL_SYSTOLIC_BLOOD_PRESSURE_LOINC_CODE);
  });
  const systolicValue = systolicComponent?.valueQuantity?.value;

  const diastolicComponent = components.find((cmp) => {
    return cmp.code?.coding?.find((coding) => coding?.code === VITAL_DIASTOLIC_BLOOD_PRESSURE_LOINC_CODE);
  });
  const diastolicValue = diastolicComponent?.valueQuantity?.value;

  return {
    systolicPressureValue: systolicValue,
    diastolicPressureValue: diastolicValue,
  };
};

export const getBloodPressureObservationMethodCodable = (
  bloodPressureDTO: VitalsBloodPressureObservationDTO
): CodeableConcept | undefined => {
  const obsMethod = bloodPressureDTO?.observationMethod;
  if (!obsMethod) return;
  if (obsMethod === VitalBloodPressureObservationMethod.Sitting) {
    return {
      coding: [
        {
          system: SNOMED_SYSTEM,
          code: VITAL_BLOOD_PRESSURE_OBS_METHOD_CODE_SITTING,
          display: 'Sitting position',
        },
      ],
    };
  }
  if (obsMethod === VitalBloodPressureObservationMethod.Standing) {
    return {
      coding: [
        {
          system: SNOMED_SYSTEM,
          code: VITAL_BLOOD_PRESSURE_OBS_METHOD_CODE_STANDING,
          display: 'Orthostatic body position',
        },
      ],
    };
  }
  if (obsMethod === VitalBloodPressureObservationMethod.Supine) {
    return {
      coding: [
        {
          system: SNOMED_SYSTEM,
          code: VITAL_BLOOD_PRESSURE_OBS_METHOD_CODE_SUPINE,
          display: 'Supine body position',
        },
      ],
    };
  }
  return undefined;
};

export const extractBloodPressureObservationMethod = (
  observation: Observation
): VitalBloodPressureObservationMethod | undefined => {
  const obsMethodCode = observation?.method?.coding?.at(0)?.code;
  if (!obsMethodCode) return;

  if (obsMethodCode === VITAL_BLOOD_PRESSURE_OBS_METHOD_CODE_SITTING) {
    return VitalBloodPressureObservationMethod.Sitting;
  }

  if (obsMethodCode === VITAL_BLOOD_PRESSURE_OBS_METHOD_CODE_STANDING) {
    return VitalBloodPressureObservationMethod.Standing;
  }

  if (obsMethodCode === VITAL_BLOOD_PRESSURE_OBS_METHOD_CODE_SUPINE) {
    return VitalBloodPressureObservationMethod.Supine;
  }
  return undefined;
};

export const extractHeartbeatObservationMethod = (
  observation: Observation
): VitalHeartbeatObservationMethod | undefined => {
  const obsMethodCode = observation?.method?.coding?.at(0)?.code;
  if (!obsMethodCode) return;

  if (obsMethodCode === VITAL_HEARTBEAT_OBS_METHOD_CODE_SITTING) {
    return VitalHeartbeatObservationMethod.Sitting;
  }

  if (obsMethodCode === VITAL_HEARTBEAT_OBS_METHOD_CODE_STANDING) {
    return VitalHeartbeatObservationMethod.Standing;
  }

  if (obsMethodCode === VITAL_HEARTBEAT_OBS_METHOD_CODE_SUPINE) {
    return VitalHeartbeatObservationMethod.Supine;
  }
  return undefined;
};

export const getOxygenSaturationObservationMethodCodable = (
  oxySatDTO: VitalsOxygenSatObservationDTO
): CodeableConcept | undefined => {
  const obsMethod = oxySatDTO?.observationMethod;
  if (!obsMethod) return;
  if (obsMethod === VitalsOxygenSatObservationMethod.OnRoomAir) {
    return {
      coding: [
        {
          system: SNOMED_SYSTEM,
          code: VITAL_OXY_SATURATION_OBS_METHOD_CODE_ROOM_AIR,
          display: 'Oxygen saturation in room air',
        },
      ],
    };
  }
  if (obsMethod === VitalsOxygenSatObservationMethod.OnSupplementalO2) {
    return {
      coding: [
        {
          system: SNOMED_SYSTEM,
          code: VITAL_OXY_SATURATION_OBS_METHOD_CODE_SUPPLEMENTAL_O2,
          display: 'Oxygen saturation with supplemental oxygen',
        },
      ],
    };
  }

  return undefined;
};

export const extractOxySaturationObservationMethod = (
  observation: Observation
): VitalsOxygenSatObservationMethod | undefined => {
  const obsMethodCode = observation?.method?.coding?.at(0)?.code;
  if (!obsMethodCode) return;

  if (obsMethodCode === VITAL_OXY_SATURATION_OBS_METHOD_CODE_ROOM_AIR) {
    return VitalsOxygenSatObservationMethod.OnRoomAir;
  }

  if (obsMethodCode === VITAL_OXY_SATURATION_OBS_METHOD_CODE_SUPPLEMENTAL_O2) {
    return VitalsOxygenSatObservationMethod.OnSupplementalO2;
  }

  return undefined;
};

export const getWeightObservationComponents = (weightDTO: VitalsWeightObservationDTO): ObservationComponent[] => {
  let result: ObservationComponent[] = [];
  const hasPatientRefused = (dto: VitalsWeightObservationDTO): dto is VitalsWeightPatientRefusedDTO =>
    !!dto.extraWeightOptions?.includes('patient_refused');

  const makeExtraWeightOptionComponent = (option: VitalsWeightOption, optionValue: boolean): ObservationComponent => {
    let optionCode = '';
    let optionLabel = '';
    if (option === 'patient_refused') {
      optionCode = VITAL_WEIGHT_PATIENT_REFUSED_OPTION_SNOMED_CODE;
      optionLabel = 'Patient Refused';
    }

    const optionCodeable: CodeableConcept = {
      coding: [
        {
          system: SNOMED_SYSTEM,
          code: optionCode,
          display: optionLabel,
        },
      ],
    };
    const weightOptionComponent: ObservationComponent = {
      code: optionCodeable,
      valueBoolean: optionValue,
    };
    return weightOptionComponent;
  };

  if (hasPatientRefused(weightDTO)) {
    return [makeExtraWeightOptionComponent('patient_refused', true)];
  }

  const extraWeightOptionsComponents = (weightDTO.extraWeightOptions ?? []).map((option) =>
    makeExtraWeightOptionComponent(option as VitalsWeightOption, true)
  );

  result = [...result, ...extraWeightOptionsComponents];

  return result;
};

export const getVisionObservationComponents = (visionDTO: VitalsVisionObservationDTO): ObservationComponent[] => {
  let result: ObservationComponent[] = [];

  if (visionDTO.leftEyeVisionText) {
    const leftEye: CodeableConcept = {
      coding: [
        {
          system: SNOMED_SYSTEM,
          code: VITAL_LEFT_EYE_SNOMED_CODE,
          display: 'Left eye observation',
        },
      ],
    };
    const leftEyeVisionComponent: ObservationComponent = {
      code: leftEye,
      valueString: visionDTO.leftEyeVisionText,
    };

    result.push(leftEyeVisionComponent);
  }

  if (visionDTO.rightEyeVisionText) {
    const rightEyeCode: CodeableConcept = {
      coding: [
        {
          system: SNOMED_SYSTEM,
          code: VITAL_RIGHT_EYE_SNOMED_CODE,
          display: 'Right eye observation',
        },
      ],
    };
    const rightEyeVisionComponent: ObservationComponent = {
      code: rightEyeCode,
      valueString: visionDTO.rightEyeVisionText,
    };

    result.push(rightEyeVisionComponent);
  }

  if (visionDTO.bothEyesVisionText) {
    const bothEyesCode: CodeableConcept = {
      coding: [
        {
          system: SNOMED_SYSTEM,
          code: VITAL_BOTH_EYES_SNOMED_CODE,
          display: 'Both eyes observation',
        },
      ],
    };
    const bothEyesVisionComponent: ObservationComponent = {
      code: bothEyesCode,
      valueString: visionDTO.bothEyesVisionText,
    };

    result.push(bothEyesVisionComponent);
  }

  const makeExtraVisionOptionComponent = (option: VitalsVisionOption, optionValue: boolean): ObservationComponent => {
    let optionCode = '';
    if (option === 'child_too_young') {
      optionCode = VITAL_VISION_CHILD_TOO_YOUNG_OPTION_SNOMED_CODE;
    } else if (option === 'with_glasses') {
      optionCode = VITAL_VISION_WITH_GLASSES_OPTION_SNOMED_CODE;
    } else if (option === 'without_glasses') {
      optionCode = VITAL_VISION_WITHOUT_GLASSES_OPTION_SNOMED_CODE;
    }

    let optionLabel = '';
    if (option === 'child_too_young') {
      optionLabel = 'Child too young';
    } else if (option === 'with_glasses') {
      optionLabel = 'With glasses';
    } else if (option === 'without_glasses') {
      optionLabel = 'Without glasses';
    }

    const optionCodeable: CodeableConcept = {
      coding: [
        {
          system: SNOMED_SYSTEM,
          code: optionCode,
          display: optionLabel,
        },
      ],
    };
    const visionOptionComponent: ObservationComponent = {
      code: optionCodeable,
      valueBoolean: optionValue,
    };
    return visionOptionComponent;
  };

  const extraVisionOptionsComponents = (visionDTO.extraVisionOptions ?? []).map((option) =>
    makeExtraVisionOptionComponent(option, true)
  );

  result = [...result, ...extraVisionOptionsComponents];

  result = [...result, ...getDotVisionScreeningComponents(visionDTO.dotVisionScreening)];

  return result;
};

const makeDotBooleanComponent = (
  system: string,
  code: string,
  display: string,
  value: boolean
): ObservationComponent => ({
  code: { coding: [{ system, code, display }] },
  valueBoolean: value,
});

// A yes/no item whose answer is a SNOMED coded value: the component code names the observable, the
// value picks the matching finding/observable concept.
const makeDotCodedAnswerComponent = (
  observableCode: string,
  observableDisplay: string,
  yes: { code: string; display: string },
  no: { code: string; display: string },
  value: boolean
): ObservationComponent => ({
  code: { coding: [{ system: SNOMED_SYSTEM, code: observableCode, display: observableDisplay }] },
  valueCodeableConcept: { coding: [{ system: SNOMED_SYSTEM, ...(value ? yes : no) }] },
});

const makeDotHorizontalFieldComponent = (
  eyeStructureCode: string,
  eyeStructureDisplay: string,
  value: number
): ObservationComponent => ({
  code: {
    coding: [
      { system: SNOMED_SYSTEM, code: VITAL_VISION_DOT_VISUAL_FIELD_SNOMED_CODE, display: 'Visual field' },
      { system: SNOMED_SYSTEM, code: VITAL_VISION_DOT_HORIZONTAL_QUALIFIER_CODE, display: 'Horizontal' },
      { system: SNOMED_SYSTEM, code: eyeStructureCode, display: eyeStructureDisplay },
    ],
  },
  valueQuantity: { value, unit: 'degrees', system: 'http://unitsofmeasure.org', code: 'deg' },
});

export const getDotVisionScreeningComponents = (dot: VitalsDotVisionScreening | undefined): ObservationComponent[] => {
  if (!dot) return [];
  const components: ObservationComponent[] = [];

  if (dot.horizontalFieldLeftDegrees != null) {
    components.push(
      makeDotHorizontalFieldComponent(
        VITAL_VISION_DOT_LEFT_EYE_STRUCTURE_CODE,
        'Structure of left eye proper',
        dot.horizontalFieldLeftDegrees
      )
    );
  }
  if (dot.horizontalFieldRightDegrees != null) {
    components.push(
      makeDotHorizontalFieldComponent(
        VITAL_VISION_DOT_RIGHT_EYE_STRUCTURE_CODE,
        'Structure of right eye proper',
        dot.horizontalFieldRightDegrees
      )
    );
  }
  if (dot.canRecognizeColors != null) {
    components.push(
      makeDotCodedAnswerComponent(
        VITAL_VISION_DOT_COLOR_RECOGNITION_SNOMED_CODE,
        'Color vision, function',
        { code: VITAL_VISION_DOT_COLOR_NORMAL_CODE, display: 'Normal color vision' },
        { code: VITAL_VISION_DOT_COLOR_ABNORMAL_CODE, display: 'Abnormal color vision' },
        dot.canRecognizeColors
      )
    );
  }
  if (dot.hasMonocularVision != null) {
    components.push(
      makeDotCodedAnswerComponent(
        VITAL_VISION_DOT_MONOCULAR_OBSERVABLE_CODE,
        'Monocular vision, function',
        { code: VITAL_VISION_DOT_MONOCULAR_OBSERVABLE_CODE, display: 'Monocular vision' },
        { code: VITAL_VISION_DOT_BINOCULAR_OBSERVABLE_CODE, display: 'Binocular vision' },
        dot.hasMonocularVision
      )
    );
  }
  if (dot.referredToSpecialist != null) {
    components.push(
      makeDotBooleanComponent(
        SNOMED_SYSTEM,
        VITAL_VISION_DOT_REFERRAL_SNOMED_CODE,
        'Patient referral',
        dot.referredToSpecialist
      )
    );
  }
  if (dot.receivedDocumentation != null) {
    components.push(
      makeDotBooleanComponent(
        VITAL_VISION_DOT_LOCAL_SYSTEM,
        VITAL_VISION_DOT_RECEIVED_DOC_CODE,
        'Received documentation from ophthalmologist or optometrist',
        dot.receivedDocumentation
      )
    );
  }
  // NOTE: the referral document is NOT stored as a component — FHIR R4 Observation.component has no
  // valueReference. It is a real FHIR DocumentReference linked via Observation.derivedFrom instead
  // (see getDotVisionDocumentDerivedFrom / extractDotVisionScreening).

  return components;
};

// The referral document lives as a DocumentReference; link it from the Observation via derivedFrom
// (reference + title as display). Only emit once the DocumentReference has actually been created.
export const getDotVisionDocumentDerivedFrom = (dot: VitalsDotVisionScreening | undefined): Reference[] => {
  if (!dot?.document?.documentReferenceId) return [];
  return [
    {
      reference: `DocumentReference/${dot.document.documentReferenceId}`,
      display: dot.document.title,
    },
  ];
};

export const extractDotVisionScreening = (
  components: ObservationComponent[],
  derivedFrom?: Reference[]
): VitalsDotVisionScreening | undefined => {
  const findByCode = (code: string): ObservationComponent | undefined =>
    components.find((cmp) => cmp.code?.coding?.some((coding) => coding?.code === code));

  const hasCode = (cmp: ObservationComponent, code: string): boolean =>
    !!cmp.code?.coding?.some((coding) => coding?.code === code);

  const valueHasCode = (cmp: ObservationComponent | undefined, code: string): boolean =>
    !!cmp?.valueCodeableConcept?.coding?.some((coding) => coding?.code === code);

  const findHorizontalField = (eyeStructureCode: string): number | undefined =>
    components.find((cmp) => hasCode(cmp, VITAL_VISION_DOT_VISUAL_FIELD_SNOMED_CODE) && hasCode(cmp, eyeStructureCode))
      ?.valueQuantity?.value;

  // For coded yes/no answers, resolve the boolean from which finding/observable the value carries.
  const codedAnswer = (observableCode: string, yesCode: string, noCode: string): boolean | undefined => {
    const cmp = findByCode(observableCode);
    if (valueHasCode(cmp, yesCode)) return true;
    if (valueHasCode(cmp, noCode)) return false;
    return undefined;
  };

  const leftField = findHorizontalField(VITAL_VISION_DOT_LEFT_EYE_STRUCTURE_CODE);
  const rightField = findHorizontalField(VITAL_VISION_DOT_RIGHT_EYE_STRUCTURE_CODE);
  const canRecognizeColors = codedAnswer(
    VITAL_VISION_DOT_COLOR_RECOGNITION_SNOMED_CODE,
    VITAL_VISION_DOT_COLOR_NORMAL_CODE,
    VITAL_VISION_DOT_COLOR_ABNORMAL_CODE
  );
  const hasMonocularVision = codedAnswer(
    VITAL_VISION_DOT_MONOCULAR_OBSERVABLE_CODE,
    VITAL_VISION_DOT_MONOCULAR_OBSERVABLE_CODE,
    VITAL_VISION_DOT_BINOCULAR_OBSERVABLE_CODE
  );
  const referredToSpecialist = findByCode(VITAL_VISION_DOT_REFERRAL_SNOMED_CODE)?.valueBoolean;
  const receivedDocumentation = findByCode(VITAL_VISION_DOT_RECEIVED_DOC_CODE)?.valueBoolean;

  // The document is linked via Observation.derivedFrom; url is not persisted (DocumentReference is
  // the source of truth), so it stays undefined when re-read from FHIR.
  const documentRef = (derivedFrom ?? []).find((ref) => ref.reference?.startsWith('DocumentReference/'));
  const document: VitalsDotVisionScreening['document'] = documentRef
    ? {
        documentReferenceId: documentRef.reference?.split('/').pop(),
        title: documentRef.display ?? 'Referral documentation',
      }
    : undefined;

  const dot: VitalsDotVisionScreening = {
    horizontalFieldLeftDegrees: leftField,
    horizontalFieldRightDegrees: rightField,
    canRecognizeColors,
    hasMonocularVision,
    referredToSpecialist,
    receivedDocumentation,
    document,
  };

  const hasAnyValue = Object.values(dot).some((v) => v !== undefined);
  return hasAnyValue ? dot : undefined;
};

export const extractVisionValues = (
  components: ObservationComponent[]
): {
  leftEyeVisText?: string;
  rightEyeVisText?: string;
  bothEyesVisText?: string;
  visionOptions?: VitalsVisionOption[];
} => {
  const leftEyeComponent = components.find((cmp) => {
    return cmp.code?.coding?.find((coding) => coding?.code === VITAL_LEFT_EYE_SNOMED_CODE);
  });

  const leftEyeVisionText = leftEyeComponent?.valueString || leftEyeComponent?.valueQuantity?.value?.toString();

  const rightEyeComponent = components.find((cmp) => {
    return cmp.code?.coding?.find((coding) => coding?.code === VITAL_RIGHT_EYE_SNOMED_CODE);
  });

  const rightEyeVisionText = rightEyeComponent?.valueString || rightEyeComponent?.valueQuantity?.value?.toString();

  const bothEyesComponent = components.find((cmp) => {
    return cmp.code?.coding?.find((coding) => coding?.code === VITAL_BOTH_EYES_SNOMED_CODE);
  });

  const bothEyesVisionText = bothEyesComponent?.valueString || bothEyesComponent?.valueQuantity?.value?.toString();

  const getVisionExtraOptionValue = (option: VitalsVisionOption): boolean | undefined => {
    let optionCode = '';
    if (option === 'child_too_young') {
      optionCode = VITAL_VISION_CHILD_TOO_YOUNG_OPTION_SNOMED_CODE;
    } else if (option === 'with_glasses') {
      optionCode = VITAL_VISION_WITH_GLASSES_OPTION_SNOMED_CODE;
    } else if (option === 'without_glasses') {
      optionCode = VITAL_VISION_WITHOUT_GLASSES_OPTION_SNOMED_CODE;
    }

    const optionComponent = components.find((cmp) => {
      return cmp.code?.coding?.find((coding) => coding?.code === optionCode);
    });
    return optionComponent?.valueBoolean;
  };

  const allExtraVisionOptions: VitalsVisionOption[] = ['child_too_young', 'with_glasses', 'without_glasses'];
  const storedExtraVisionOptions = allExtraVisionOptions.filter((option) => getVisionExtraOptionValue(option) === true);

  return {
    leftEyeVisText: leftEyeVisionText,
    rightEyeVisText: rightEyeVisionText,
    bothEyesVisText: bothEyesVisionText,
    visionOptions: storedExtraVisionOptions,
  };
};

export function isVitalObservation(data: ObservationDTO): data is VitalsObservationDTO {
  return (
    isWeightVitalObservation(data) ||
    isHeightVitalObservation(data) ||
    isBMIVitalObservation(data) ||
    isTemperatureVitalObservation(data) ||
    isHeartbeatVitalObservation(data) ||
    isBloodPressureVitalObservation(data) ||
    isOxygenSaturationVitalObservation(data) ||
    isRespirationRateVitalObservation(data) ||
    isVisionVitalObservation(data) ||
    isLastMenstrualPeriodVitalObservation(data)
  );
}

export function isBMIVitalObservation(data: ObservationDTO): data is VitalsBMIObservationDTO {
  return data.field === VitalFieldNames.VitalBMI;
}

export function isWeightVitalObservation(data: ObservationDTO): data is VitalsWeightObservationDTO {
  const fieldName = data.field;
  return fieldName === VitalFieldNames.VitalWeight;
}

export function isHeightVitalObservation(data: ObservationDTO): data is VitalsHeightObservationDTO {
  const fieldName = data.field;
  return fieldName === VitalFieldNames.VitalHeight;
}

export function isTemperatureVitalObservation(data: ObservationDTO): data is VitalsTemperatureObservationDTO {
  const fieldName = data.field;
  return fieldName === VitalFieldNames.VitalTemperature;
}

export function isHeartbeatVitalObservation(data: ObservationDTO): data is VitalsHeartbeatObservationDTO {
  const fieldName = data.field;
  return fieldName === VitalFieldNames.VitalHeartbeat;
}

export function isBloodPressureVitalObservation(data: ObservationDTO): data is VitalsBloodPressureObservationDTO {
  const fieldName = data.field;
  return fieldName === VitalFieldNames.VitalBloodPressure;
}

export function isVisionVitalObservation(data: ObservationDTO): data is VitalsVisionObservationDTO {
  const fieldName = data.field;
  return fieldName === VitalFieldNames.VitalVision;
}

export function isRespirationRateVitalObservation(data: ObservationDTO): data is VitalsRespirationRateObservationDTO {
  const fieldName = data.field;
  return fieldName === VitalFieldNames.VitalRespirationRate;
}

export function isOxygenSaturationVitalObservation(data: ObservationDTO): data is VitalsOxygenSatObservationDTO {
  const fieldName = data.field;
  return fieldName === VitalFieldNames.VitalOxygenSaturation;
}

export function isLastMenstrualPeriodVitalObservation(
  data: ObservationDTO
): data is VitalsLastMenstrualPeriodObservationDTO {
  const fieldName = data.field;
  return fieldName === VitalFieldNames.VitalLastMenstrualPeriod;
}

export function toVitalTemperatureObservationMethod(
  value: string | undefined
): VitalTemperatureObservationMethod | undefined {
  if (Object.values(VitalTemperatureObservationMethod).includes(value as VitalTemperatureObservationMethod)) {
    return value as VitalTemperatureObservationMethod;
  }
  return undefined;
}

export function toVitalBloodPressureObservationMethod(
  value: string | undefined
): VitalBloodPressureObservationMethod | undefined {
  if (Object.values(VitalBloodPressureObservationMethod).includes(value as VitalBloodPressureObservationMethod)) {
    return value as VitalBloodPressureObservationMethod;
  }
  return undefined;
}

export function toVitalHeartbeatObservationMethod(
  value: string | undefined
): VitalHeartbeatObservationMethod | undefined {
  if (Object.values(VitalHeartbeatObservationMethod).includes(value as VitalHeartbeatObservationMethod)) {
    return value as VitalHeartbeatObservationMethod;
  }
  return undefined;
}

export function toVitalOxygenSatObservationMethod(
  value: string | undefined
): VitalsOxygenSatObservationMethod | undefined {
  if (Object.values(VitalsOxygenSatObservationMethod).includes(value as VitalsOxygenSatObservationMethod)) {
    return value as VitalsOxygenSatObservationMethod;
  }
  return undefined;
}

export function fillVitalObservationAttributes(
  baseResource: Observation,
  vitalDTO: VitalsObservationDTO,
  patientDOB: string | undefined,
  vitalsAlertConfig: VitalsSchema | undefined
): Observation {
  if (isTemperatureVitalObservation(vitalDTO)) {
    const temperatureDTO = vitalDTO as VitalsTemperatureObservationDTO;
    return {
      ...baseResource,
      valueQuantity: { value: temperatureDTO.value, system: 'http://unitsofmeasure.org', unit: 'C', code: 'Cel' },
      method: getTempObservationMethodCodable(temperatureDTO),
    };
  }

  if (isHeartbeatVitalObservation(vitalDTO)) {
    const heartbeatDTO = vitalDTO as VitalsHeartbeatObservationDTO;
    return {
      ...baseResource,
      valueQuantity: { value: heartbeatDTO.value, system: 'http://unitsofmeasure.org', unit: 'beats/min' },
      method: getHeartbeatObservationMethodCodable(heartbeatDTO),
    };
  }

  if (isBloodPressureVitalObservation(vitalDTO)) {
    const bloodPressureDTO = vitalDTO as VitalsBloodPressureObservationDTO;
    return {
      ...baseResource,
      component: getBloodPressureObservationComponents(bloodPressureDTO, patientDOB, vitalsAlertConfig),
      method: getBloodPressureObservationMethodCodable(bloodPressureDTO),
    };
  }

  if (isOxygenSaturationVitalObservation(vitalDTO)) {
    const oxySatDTO = vitalDTO as VitalsOxygenSatObservationDTO;
    return {
      ...baseResource,
      valueQuantity: { value: oxySatDTO.value, system: 'http://unitsofmeasure.org', unit: 'beats/min' },
      method: getOxygenSaturationObservationMethodCodable(oxySatDTO),
    };
  }

  if (isRespirationRateVitalObservation(vitalDTO)) {
    const respirationRateDTO = vitalDTO as VitalsRespirationRateObservationDTO;
    return {
      ...baseResource,
      valueQuantity: { value: respirationRateDTO.value, system: 'http://unitsofmeasure.org', unit: 'respirations/min' },
    };
  }

  if (isWeightVitalObservation(vitalDTO)) {
    const weightDTO = vitalDTO as VitalsWeightObservationDTO;
    return {
      ...baseResource,
      code: { coding: [{ system: LOINC_SYSTEM, code: '29463-7', display: 'Body weight' }] },
      valueQuantity: weightDTO.value
        ? { value: weightDTO.value, system: 'http://unitsofmeasure.org', unit: 'kg' }
        : undefined,
      component: getWeightObservationComponents(weightDTO),
    };
  }

  if (isHeightVitalObservation(vitalDTO)) {
    const heightDTO = vitalDTO as VitalsHeightObservationDTO;
    return {
      ...baseResource,
      code: { coding: [{ system: LOINC_SYSTEM, code: '8302-2', display: 'Body height' }] },
      valueQuantity: { value: heightDTO.value, system: 'http://unitsofmeasure.org', unit: 'cm' },
    };
  }

  if (isBMIVitalObservation(vitalDTO)) {
    const bmiDTO = vitalDTO as VitalsBMIObservationDTO;
    return {
      ...baseResource,
      code: {
        coding: [{ system: LOINC_SYSTEM, code: VITAL_BMI_LOINC_CODE, display: 'Body mass index (BMI) [Ratio]' }],
      },
      valueQuantity: { value: bmiDTO.value, system: 'http://unitsofmeasure.org', unit: 'kg/m2' },
    };
  }

  if (isVisionVitalObservation(vitalDTO)) {
    const visionDTO = vitalDTO as VitalsVisionObservationDTO;
    const derivedFrom = getDotVisionDocumentDerivedFrom(visionDTO.dotVisionScreening);
    return {
      ...baseResource,
      component: getVisionObservationComponents(visionDTO),
      ...(derivedFrom.length > 0 ? { derivedFrom } : {}),
    };
  }

  if (isLastMenstrualPeriodVitalObservation(vitalDTO)) {
    const lmpDTO = vitalDTO as VitalsLastMenstrualPeriodObservationDTO;
    return {
      ...baseResource,
      code: {
        coding: [
          {
            system: LOINC_SYSTEM,
            code: VITAL_LAST_MENSTRUAL_PERIOD_LOINC_CODE,
            display: 'Last menstrual period start date',
          },
        ],
      },
      valueDateTime: lmpDTO.value || undefined,
      component: lmpDTO.isUnsure
        ? [
            {
              code: {
                coding: [
                  {
                    system: SNOMED_SYSTEM,
                    code: VITAL_LAST_MENSTRUAL_PERIOD_UNSURE_OPTION_SNOMED_CODE,
                    display: 'Unknown',
                  },
                ],
              },
              valueBoolean: true,
            },
          ]
        : undefined,
    };
  }

  //TODO: is it ok to throw error in this case ?
  throw new Error('fillVitalObservationAttributes() unknown VitalsObservationDTO type');
}

export function makeVitalsObservationDTO(observation: Observation): VitalsObservationDTO | undefined {
  const fieldName = observation.meta?.tag?.[0].code as VitalFieldNames;
  if (!fieldName) return;
  if (!Object.values(VitalFieldNames).includes(fieldName)) return;

  const resourceIdVal = observation.id;
  const patientIdVal = observation.subject?.reference?.split('/')[1] ?? '';
  const encounterIdVal = observation.encounter?.reference?.split('/')[1] ?? '';
  const authorIdVal = observation.performer?.at(0)?.reference?.split('/')?.[1];
  const lastUpdatedVal = observation.effectiveDateTime;

  const baseProps: Partial<VitalsBaseObservationDTO> = {
    resourceId: resourceIdVal,
    patientId: patientIdVal,
    encounterId: encounterIdVal,
    authorId: authorIdVal,
    lastUpdated: lastUpdatedVal,
  };

  if (fieldName === VitalFieldNames.VitalTemperature) {
    const obsNumericalValue = observation.valueQuantity?.value ?? 0;
    const result: VitalsTemperatureObservationDTO = {
      ...baseProps,
      field: VitalFieldNames.VitalTemperature,
      value: obsNumericalValue,
      observationMethod: extractTemperatureObservationMethod(observation),
    };
    return result;
  }

  if (fieldName === VitalFieldNames.VitalHeartbeat) {
    const obsNumericalValue = observation.valueQuantity?.value ?? 0;
    const result: VitalsHeartbeatObservationDTO = {
      ...baseProps,
      field: VitalFieldNames.VitalHeartbeat,
      value: obsNumericalValue,
      observationMethod: extractHeartbeatObservationMethod(observation),
    };
    return result;
  }

  if (fieldName === VitalFieldNames.VitalBloodPressure) {
    const bloodPressures = extractBloodPressureValues(observation.component ?? []);
    const result: VitalsBloodPressureObservationDTO = {
      ...baseProps,
      field: VitalFieldNames.VitalBloodPressure,
      systolicPressure: bloodPressures.systolicPressureValue ?? 0,
      diastolicPressure: bloodPressures.diastolicPressureValue ?? 0,
      observationMethod: extractBloodPressureObservationMethod(observation),
    };
    return result;
  }

  if (fieldName === VitalFieldNames.VitalOxygenSaturation) {
    const obsNumericalValue = observation.valueQuantity?.value ?? 0;
    const result: VitalsOxygenSatObservationDTO = {
      ...baseProps,
      field: VitalFieldNames.VitalOxygenSaturation,
      value: obsNumericalValue,
      observationMethod: extractOxySaturationObservationMethod(observation),
    };
    return result;
  }

  if (fieldName === VitalFieldNames.VitalRespirationRate) {
    const obsNumericalValue = observation.valueQuantity?.value ?? 0;
    const result: VitalsRespirationRateObservationDTO = {
      ...baseProps,
      field: VitalFieldNames.VitalRespirationRate,
      value: obsNumericalValue,
    };
    return result;
  }

  if (fieldName === VitalFieldNames.VitalWeight) {
    const obsNumericalValue = observation.valueQuantity?.value;
    const weightValues = extractWeightValues(observation.component ?? []);
    const hasPatientRefused = weightValues.weightOptions?.includes('patient_refused');

    if (hasPatientRefused) {
      const result: VitalsWeightObservationDTO = {
        ...baseProps,
        field: VitalFieldNames.VitalWeight,
        extraWeightOptions: ['patient_refused'],
      };
      return result;
    } else {
      const result: VitalsWeightObservationDTO = {
        ...baseProps,
        field: VitalFieldNames.VitalWeight,
        value: obsNumericalValue ?? 0,
        extraWeightOptions: weightValues.weightOptions,
      };
      return result;
    }
  }

  if (fieldName === VitalFieldNames.VitalHeight) {
    const obsNumericalValue = observation.valueQuantity?.value ?? 0;
    const result: VitalsHeightObservationDTO = {
      ...baseProps,
      field: VitalFieldNames.VitalHeight,
      value: obsNumericalValue,
    };
    return result;
  }

  if (fieldName === VitalFieldNames.VitalBMI) {
    const obsNumericalValue = observation.valueQuantity?.value ?? 0;
    const result: VitalsBMIObservationDTO = {
      ...baseProps,
      field: VitalFieldNames.VitalBMI,
      value: obsNumericalValue,
    };
    return result;
  }

  if (fieldName === VitalFieldNames.VitalVision) {
    const visionValues = extractVisionValues(observation.component ?? []);
    const result: VitalsVisionObservationDTO = {
      ...baseProps,
      field: VitalFieldNames.VitalVision,
      leftEyeVisionText: visionValues.leftEyeVisText ?? '',
      rightEyeVisionText: visionValues.rightEyeVisText ?? '',
      bothEyesVisionText: visionValues.bothEyesVisText,
      extraVisionOptions: visionValues.visionOptions,
      dotVisionScreening: extractDotVisionScreening(observation.component ?? [], observation.derivedFrom),
    };
    return result;
  }

  if (fieldName === VitalFieldNames.VitalLastMenstrualPeriod) {
    const hasUnsure = observation.component?.some(
      (cmp) =>
        cmp.code?.coding?.some((coding) => coding.code === VITAL_LAST_MENSTRUAL_PERIOD_UNSURE_OPTION_SNOMED_CODE) &&
        cmp.valueBoolean === true
    );

    const result: VitalsLastMenstrualPeriodObservationDTO = {
      ...baseProps,
      field: VitalFieldNames.VitalLastMenstrualPeriod,
      value: observation.valueDateTime ?? '',
      isUnsure: hasUnsure,
    };
    return result;
  }

  return undefined;
}

const extractWeightValues = (
  components: ObservationComponent[]
): {
  weightOptions?: VitalsWeightOption[];
} => {
  const getWeightExtraOptionValue = (option: VitalsWeightOption): boolean | undefined => {
    let optionCode = '';

    if (option === 'patient_refused') {
      optionCode = VITAL_WEIGHT_PATIENT_REFUSED_OPTION_SNOMED_CODE;
    }

    const optionComponent = components.find((cmp) => {
      return cmp.code?.coding?.find((coding) => coding?.code === optionCode);
    });

    return optionComponent?.valueBoolean;
  };

  const allExtraWeightOptions: VitalsWeightOption[] = ['patient_refused'];
  const storedExtraWeightOptions = allExtraWeightOptions.filter((option) => getWeightExtraOptionValue(option) === true);

  return {
    weightOptions: storedExtraWeightOptions,
  };
};

export function parseLastMenstrualPeriodObservation(
  observation: Observation,
  performer: Practitioner
): VitalsLastMenstrualPeriodObservationDTO | undefined {
  const fieldCode = observation?.meta?.tag?.find(
    (tag) => tag.system === `${PRIVATE_EXTENSION_BASE_URL}/${PATIENT_VITALS_META_SYSTEM}`
  )?.code;

  if (fieldCode !== VitalFieldNames.VitalLastMenstrualPeriod) return undefined;

  const components = observation.component || [];
  const hasUnsure = components.some(
    (cmp) =>
      cmp.code?.coding?.some(
        (coding) =>
          coding.code === VITAL_LAST_MENSTRUAL_PERIOD_UNSURE_OPTION_SNOMED_CODE && coding.system === SNOMED_SYSTEM
      ) && cmp.valueBoolean === true
  );

  const value = observation.valueDateTime;
  if (value === undefined && !hasUnsure) return undefined;

  return {
    resourceId: observation.id,
    field: VitalFieldNames.VitalLastMenstrualPeriod,
    value: value ?? '',
    isUnsure: hasUnsure,
    authorId: performer.id,
    authorName: getFullName(performer),
    lastUpdated: observation.effectiveDateTime || '',
  };
}
